import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import {
  IGetServiceRequesitionResponse,
  IServiceRequesition,
} from "./service-request.types";
import { server_base_endpoints } from "../../api/server-base-endpoints";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import produce from "immer";
import { getUniqueId } from "../../helpers";

export const FETCH_SERVICE_REQUISITION_LIST_PROGRESS =
  "FETCH_SERVICE_REQUISITION_LIST_PROGRESS";
export const FETCH_SERVICE_REQUISITION_LIST_SUCCESS =
  "FETCH_SERVICE_REQUISITION_LIST_SUCCESS";
export const FETCH_SERVICE_REQUISITION_LIST_FAILED =
  "FETCH_SERVICE_REQUISITION_LIST_FAILED";

export const fetchServiceRequesitionListProgress = () =>
  action(FETCH_SERVICE_REQUISITION_LIST_PROGRESS);
export const fetchServiceRequesitionListSuccess = (
  data: IServiceRequesition[],
  totalRecords: number,
) => action(FETCH_SERVICE_REQUISITION_LIST_SUCCESS, { data, totalRecords });
export const fetchServiceRequesitionListFailed = () =>
  action(FETCH_SERVICE_REQUISITION_LIST_FAILED);

export const fetchServiceRequesitionListAsync =
  (
    queryParams: IQueryParams,
    type: "GENERAL" | "APPROVAL",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchServiceRequesitionListProgress());
      let url = `${server_base_endpoints.service.get_service_requesition}${searchQuery}`;
      if (type === "APPROVAL") {
        url = `/dynamicApproval/get-approval${searchQuery}&table_name=latest_service_requesition`;
      }
      const res = await api.get(url);
      const data: IServiceRequesition[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchServiceRequesitionListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchServiceRequesitionListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleServiceRequesitionAsync =
  (
    payload: IServiceRequesition,
    onCallback: (
      isSuccess: boolean,
      serviceRequest?: IServiceRequesition,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        title: payload.service_requesition_name,
      };

      const { paths } = await uploadMultipleFile(
        payload.attachment || [],
        "JOB_BOARD",
        asPayload,
      );
      const {
        create_ts,
        insert_ts,
        // created_by_name,
        // requested_by_uuid,
        // approval_uuid,
        // is_user_approver,
        // billing_company_branch_address_state,
        prefix,
        financial_year,
        ...rest
      } = payload;
      const res = await api.post(
        server_base_endpoints.service.upsert_service_requesition,
        {
          ...rest,
          attachment: paths,
        },
      );
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "SR saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_SINGLE_SERVICE_REQUISITION_PROGRESS =
  "FETCH_SINGLE_SERVICE_REQUISITION_PROGRESS";
export const FETCH_SINGLE_SERVICE_REQUISITION_SUCCESS =
  "FETCH_SINGLE_SERVICE_REQUISITION_SUCCESS";
export const FETCH_SINGLE_SERVICE_REQUISITION_FAILED =
  "FETCH_SINGLE_SERVICE_REQUISITION_FAILED";

export const fetchSingleServiceRequesitionProgress = () =>
  action(FETCH_SINGLE_SERVICE_REQUISITION_PROGRESS);
export const fetchSingleServiceRequesitionSuccess = (
  data: IServiceRequesition,
) => action(FETCH_SINGLE_SERVICE_REQUISITION_SUCCESS, { data });
export const fetchSingleServiceRequesitionFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_SERVICE_REQUISITION_FAILED, { errorMessage });

export const fetchSingleServiceRequesitionAsync =
  (
    uuid: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleServiceRequesitionProgress());
      let url = `${server_base_endpoints.service.get_service_requesition}?service_requesition_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IGetServiceRequesitionResponse[] = res.data.data;
      if (data.length > 0) {
        const { attachment, ...rest } = data[0];
        const newRespponse: IServiceRequesition = produce(
          { ...rest, attachment: [] as IFileUpload[] },
          (draftState) => {
            if (Array.isArray(attachment) && attachment.length > 0) {
              draftState.attachment = attachment.map((path) => ({
                key: getUniqueId(),
                file: null,
                type: "SERVER",
                name: getFileNameFromUrl(path as unknown as string),
                path: path,
              }));
            }
          },
        );
        dispatch(fetchSingleServiceRequesitionSuccess(newRespponse));
      } else {
        dispatch(
          fetchSingleServiceRequesitionFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(
        fetchSingleServiceRequesitionFailed("Something went to be wrong!"),
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_SINGLE_SERVICE_REQUISITION =
  "CLEAR_SINGLE_SERVICE_REQUISITION";
export const CLEAR_SERVICE_REQUISITION_STATE =
  "CLEAR_SERVICE_REQUISITION_STATE";

export const clearSingleServiceRequesition = () =>
  action(CLEAR_SINGLE_SERVICE_REQUISITION);
export const clearServiceRequesitionState = () =>
  action(CLEAR_SERVICE_REQUISITION_STATE);

// ##########################################################################################
// ############################ Approval List#################################
// ##########################################################################################

export const FETCH_SERVICE_REQUISITION_APPROVAL_LIST_PROGRESS =
  "FETCH_SERVICE_REQUISITION_APPROVAL_LIST_PROGRESS";
export const FETCH_SERVICE_REQUISITION_APPROVAL_LIST_SUCCESS =
  "FETCH_SERVICE_REQUISITION_APPROVAL_LIST_SUCCESS";
export const FETCH_SERVICE_REQUISITION_APPROVAL_LIST_FAILED =
  "FETCH_SERVICE_REQUISITION_APPROVAL_LIST_FAILED";

export const fetchServiceRequesitionApprovalListProgress = () =>
  action(FETCH_SERVICE_REQUISITION_APPROVAL_LIST_PROGRESS);
export const fetchServiceRequesitionApprovalListSuccess = (
  data: IServiceRequesition[],
  totalRecords: number,
) =>
  action(FETCH_SERVICE_REQUISITION_APPROVAL_LIST_SUCCESS, {
    data,
    totalRecords,
  });
export const fetchServiceRequesitionApprovalListFailed = () =>
  action(FETCH_SERVICE_REQUISITION_APPROVAL_LIST_FAILED);

export const fetchServiceRequesitionApprovalListAsync =
  (
    queryParams: IQueryParams,
    table_name: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchServiceRequesitionApprovalListProgress());
      let searchQuery = getSearchQuery(queryParams);
      if (billing_company_uuid) {
        searchQuery += `&billing_company_uuid=${billing_company_uuid}`;
      }
      let finalUrl = `${server_base_endpoints.approvals.get_approval}${searchQuery}&table_name=${table_name}`;
      const res = await api.get(finalUrl);
      const data: IServiceRequesition[] = res.data.data;
      dispatch(
        fetchServiceRequesitionApprovalListSuccess(data, res.data.totalRecords),
      );
    } catch (err: any) {
      dispatch(fetchServiceRequesitionApprovalListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };
